import { useCustomerSearch, type CustomerWithLocations } from "@/features/customer-360/api";
import { useDispatchSearch, type DispatchSearchResult } from "./useDispatchSearch";

// ============================================================================
// useUniversalSearch — orchestrator for the header dropdown.
//
// Composes the existing customer search (per-customer-360 hook) with the new
// dispatch search. Both run in parallel via React Query; results are sliced
// to the dropdown's display limits here, full counts are preserved for the
// "View all" footer.
//
// v1 (BUILD-23): two categories — Customers + Dispatches. Techs deferred to
// v1.5. Properties wait on CUST-06. POs / Invoices / Equipment / Agreements /
// Vendors slot in as their underlying tables land.
//
// Caller is expected to debounce the input — the underlying hooks have
// their own React Query staleTime caches but skipping rapid intermediate
// queries on every keystroke is cheaper still.
// ============================================================================

const DISPLAY_LIMIT = 4;

export interface UniversalSearchState {
  query: string;
  isLoading: boolean;
  hasAnyResults: boolean;
  customers: {
    rows: CustomerWithLocations[];
    totalAvailable: number;
    hasMore: boolean;
  };
  dispatches: {
    rows: DispatchSearchResult[];
    totalAvailable: number;
    hasMore: boolean;
  };
}

export function useUniversalSearch(query: string): UniversalSearchState {
  const trimmed = query.trim();
  const enabled = trimmed.length >= 2;

  const customerQ = useCustomerSearch(enabled ? trimmed : "");
  const dispatchQ = useDispatchSearch(enabled ? trimmed : "");

  const allCustomers = customerQ.data ?? [];
  const allDispatches = dispatchQ.data ?? [];

  return {
    query: trimmed,
    isLoading: customerQ.isLoading || dispatchQ.isLoading,
    hasAnyResults: allCustomers.length > 0 || allDispatches.length > 0,
    customers: {
      rows: allCustomers.slice(0, DISPLAY_LIMIT),
      totalAvailable: allCustomers.length,
      hasMore: allCustomers.length > DISPLAY_LIMIT,
    },
    dispatches: {
      rows: allDispatches.slice(0, DISPLAY_LIMIT),
      totalAvailable: allDispatches.length,
      hasMore: allDispatches.length > DISPLAY_LIMIT,
    },
  };
}
